import { curve, haversine } from './geo';
import type { LatLng } from './types';

/** A line as MapLibre wants it: [lng, lat] pairs. */
export type Line = [number, number][];

/**
 * Decode an encoded polyline (Google's algorithm; the server sends precision 5) into [lng, lat] pairs.
 * Bad input gives what could be read so far (never throws).
 */
export function decodePolyline(str: string, precision = 5): Line {
  const k = 10 ** precision;
  const out: Line = [];
  let i = 0;
  let lat = 0;
  let lng = 0;
  const next = (): number | null => {
    let result = 0;
    let shift = 0;
    let b: number;
    do {
      if (i >= str.length) return null;
      b = str.charCodeAt(i++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (i < str.length) {
    const dLat = next();
    const dLng = next();
    if (dLat == null || dLng == null) break;
    lat += dLat;
    lng += dLng;
    out.push([lng / k, lat / k]);
  }
  return out;
}

const ll = (p: [number, number]): LatLng => ({ lng: p[0], lat: p[1] });

/**
 * The trip's line: the server's road route when it sent one (`routePolyline`), otherwise the drawn curve. The road
 * line is pinned to the exact pickup and drop so it meets the markers.
 */
export function routeLine(a: LatLng, b: LatLng, polyline?: string | null, bend?: number, steps?: number): Line {
  const road = polyline ? decodePolyline(polyline) : [];
  if (road.length < 2) return curve(a, b, bend, steps);
  return [[a.lng, a.lat], ...road, [b.lng, b.lat]];
}

/** Cumulative metres at each vertex. */
export function lengths(line: Line): number[] {
  const out = [0];
  for (let i = 1; i < line.length; i++) out.push(out[i - 1] + haversine(ll(line[i - 1]), ll(line[i])));
  return out;
}

/**
 * The closest point of the line to `p`: how far along it is (metres), how far off the line `p` is, and the segment.
 * Planar projection per segment (fine at street scale), distances in metres.
 */
export function project(line: Line, p: LatLng, cum = lengths(line)): { along: number; off: number; seg: number; at: LatLng } {
  let best = { along: 0, off: Infinity, seg: 0, at: line.length ? ll(line[0]) : p };
  const kx = Math.cos((p.lat * Math.PI) / 180);
  for (let i = 1; i < line.length; i++) {
    const [ax, ay] = line[i - 1];
    const [bx, by] = line[i];
    const dx = (bx - ax) * kx;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, (((p.lng - ax) * kx) * dx + (p.lat - ay) * dy) / len2)) : 0;
    const at = { lng: ax + (bx - ax) * t, lat: ay + (by - ay) * t };
    const off = haversine(at, p);
    if (off < best.off) best = { along: cum[i - 1] + (cum[i] - cum[i - 1]) * t, off, seg: i - 1, at };
  }
  return best;
}

/** The point `along` metres down the line. */
export function pointAt(line: Line, along: number, cum = lengths(line)): LatLng {
  if (!line.length) return { lat: 0, lng: 0 };
  if (along <= 0) return ll(line[0]);
  for (let i = 1; i < line.length; i++) {
    if (cum[i] >= along) {
      const seg = cum[i] - cum[i - 1];
      const t = seg ? (along - cum[i - 1]) / seg : 0;
      return { lng: line[i - 1][0] + (line[i][0] - line[i - 1][0]) * t, lat: line[i - 1][1] + (line[i][1] - line[i - 1][1]) * t };
    }
  }
  return ll(line[line.length - 1]);
}

/** Split the line where the vehicle is: the part already driven and the part still to go (both meet at `at`). */
export function splitAt(line: Line, at: LatLng, cum = lengths(line)): { done: Line; rest: Line } {
  if (line.length < 2) return { done: [], rest: line };
  const { seg } = project(line, at, cum);
  const p: [number, number] = [at.lng, at.lat];
  return { done: [...line.slice(0, seg + 1), p], rest: [p, ...line.slice(seg + 1)] };
}
